import { SiteFooter } from "@/components/SiteFooter";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { ArrowLeft, Clock, Calendar, Trophy, Music, Mic, UtensilsCrossed, MapPin, ArrowRight, ImageIcon } from "lucide-react";
import { getPublishedEvent } from "@/lib/events.functions";
import { useReservationModal } from "@/context/ReservationContext";
import { Lightbox } from "@/components/Lightbox";
import { useTrackEvent } from "@/lib/analytics";

export const Route = createFileRoute("/events/$slug")({
  loader: async ({ params }) => {
    try {
      const event = await getPublishedEvent({ data: { id: params.slug } });
      return { event };
    } catch {
      return { event: null };
    }
  },
  head: ({ loaderData, params }) => {
    const e = loaderData?.event;
    if (!e) {
      return { meta: [{ title: "Event — Plunkets Pub Dublin" }, { name: "robots", content: "noindex" }] };
    }
    const when = new Date(e.starts_at).toLocaleString("en-IE", {
      weekday: "long", day: "2-digit", month: "long", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Dublin",
    });
    const title = `${e.title} — Plunkets Pub Dublin`;
    const desc = e.notes?.slice(0, 155) ?? `${e.title} — ${when} at Plunkets Pub, Dublin city centre. Walk in or reserve ahead.`;
    const url = `https://plunkets.ie/events/${params.slug}`;
    const shareImg = e.banner_url
      ? (e.banner_url.startsWith("http") ? e.banner_url : `https://plunkets.ie${e.banner_url.startsWith("/") ? "" : "/"}${e.banner_url}`)
      : "https://plunkets.ie/og-events.jpg?v=2";
    const imageMeta = [
      { property: "og:image", content: shareImg },
      { property: "og:image:secure_url", content: shareImg },
      { property: "og:image:alt", content: e.title },
      ...(!e.banner_url ? [
        { property: "og:image:width", content: "1200" },
        { property: "og:image:height", content: "630" },
      ] : []),
      { name: "twitter:image", content: shareImg },
      { name: "twitter:image:alt", content: e.title },
    ];
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
        { property: "og:site_name", content: "Plunkets Pub Dublin" },
        { property: "og:locale", content: "en_IE" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:site", content: "@plunketspub" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: desc },
        ...imageMeta,
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [{
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Event",
          name: e.title,
          description: e.notes ?? undefined,
          startDate: e.starts_at,
          endDate: e.ends_at ?? undefined,
          eventStatus: "https://schema.org/EventScheduled",
          eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
          image: e.banner_url ?? undefined,
          url,
          location: {
            "@type": "Place",
            name: "Plunkets Pub",
            address: {
              "@type": "PostalAddress",
              streetAddress: "Middle Abbey Street",
              addressLocality: "Dublin",
              addressRegion: "Dublin 1",
              postalCode: "D01",
              addressCountry: "IE",
            },
          },
          organizer: { "@type": "Organization", name: "Plunkets Pub Dublin", url: "https://plunkets.ie/" },
        }),
      }],
    };
  },
  component: EventDetail,
  notFoundComponent: () => (
    <main className="flex min-h-screen items-center justify-center bg-background px-5 text-center">
      <div>
        <h1 className="font-display text-3xl">Event not found</h1>
        <p className="mt-2 text-sm text-muted-foreground">It may have ended or been unpublished.</p>
        <Link to="/events" className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary px-6 py-2.5 text-sm font-semibold text-primary-foreground">
          <ArrowLeft size={14} /> All events
        </Link>
      </div>
    </main>
  ),
  errorComponent: ({ error }) => (
    <main className="flex min-h-screen items-center justify-center bg-background px-5 text-center">
      <div>
        <h1 className="font-display text-2xl">Something went wrong</h1>
        <p className="mt-2 text-sm text-muted-foreground">{error instanceof Error ? error.message : String(error)}</p>
        <Link to="/events" className="mt-6 inline-flex items-center gap-2 text-sm text-primary">
          <ArrowLeft size={14} /> Back to events
        </Link>
      </div>
    </main>
  ),
});

function iconForTag(tag: string | null) {
  const t = (tag ?? "").toLowerCase();
  if (t.includes("sport") || t.includes("match")) return Trophy;
  if (t.includes("music") || t.includes("dj") || t.includes("trad")) return Music;
  if (t.includes("karaoke")) return Mic;
  if (t.includes("food") || t.includes("special")) return UtensilsCrossed;
  return Calendar;
}

function bookingTypeForTag(tag: string | null) {
  const t = (tag ?? "").toLowerCase();
  if (t.includes("sport") || t.includes("match")) return "sports_viewing" as const;
  if (t.includes("group")) return "group_booking" as const;
  return "food_and_drinks" as const;
}

function formatLong(iso: string) {
  return new Date(iso).toLocaleString("en-IE", {
    weekday: "long", day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
}

function EventDetail() {
  const { slug: id } = Route.useParams();
  useTrackEvent("event_page_view", { page_type: "event_detail", event_slug: id });
  const getEvent = useServerFn(getPublishedEvent);
  const { data: event, isLoading } = useQuery({
    queryKey: ["public-event", id],
    queryFn: () => getEvent({ data: { id } }),
    staleTime: 0,
    refetchOnMount: "always",
  });
  const { open: openModal } = useReservationModal();
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  if (isLoading) {
    return <main className="min-h-screen bg-background px-5 py-24 text-center text-muted-foreground">Loading event…</main>;
  }
  if (!event) throw notFound();

  const Icon = iconForTag(event.tag);

  return (
    <main className="min-h-screen bg-background pb-24 text-foreground md:pb-0">
      {/* Banner */}
      {event.banner_url ? (
        <section className="relative h-[55vh] min-h-[360px] w-full overflow-hidden sm:h-[60vh] sm:min-h-[420px]">
          <img src={event.banner_url} alt={event.title} className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/70 to-background/20" />
          <div className="relative z-10 mx-auto flex h-full max-w-4xl flex-col justify-end px-5 pb-10 md:px-8 md:pb-20">
            <Link to="/events" className="mb-5 inline-flex w-fit items-center gap-2 rounded-full bg-background/60 px-4 py-1.5 text-xs uppercase tracking-wider text-foreground backdrop-blur hover:bg-background/80">
              <ArrowLeft size={12} /> All events
            </Link>
            <p className="flex items-center gap-2 text-[11px] uppercase tracking-[0.35em] text-primary sm:text-xs sm:tracking-[0.4em]">
              <Icon size={14} /> {event.tag ?? "Event"}
            </p>
            <h1 className="mt-3 font-display text-3xl leading-[0.95] tracking-tight sm:text-4xl md:text-6xl">{event.title}</h1>
            <p className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground md:text-base">
              <Clock size={14} className="shrink-0" /> {formatLong(event.starts_at)}
              {event.ends_at && (
                <span className="text-muted-foreground/80">
                  → {new Date(event.ends_at).toLocaleTimeString("en-IE", { hour: "2-digit", minute: "2-digit" })}
                </span>
              )}
            </p>
          </div>
        </section>
      ) : (
        <section className="px-5 pb-2 pt-12 md:px-8 md:pt-24">
          <div className="mx-auto max-w-4xl">
            <Link to="/events" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary">
              <ArrowLeft size={16} /> All events
            </Link>
            <p className="mt-8 flex items-center gap-2 text-[11px] uppercase tracking-[0.35em] text-primary sm:text-xs sm:tracking-[0.4em]">
              <Icon size={14} /> {event.tag ?? "Event"}
            </p>
            <h1 className="mt-3 font-display text-3xl leading-[0.95] tracking-tight sm:text-4xl md:text-6xl">{event.title}</h1>
            <p className="mt-3 flex items-center gap-2 text-sm text-muted-foreground">
              <Clock size={14} /> {formatLong(event.starts_at)}
            </p>
          </div>
        </section>
      )}

      {/* Body */}
      <section className="px-5 py-12 md:px-8 md:py-20">
        <div className="mx-auto grid max-w-4xl gap-8 md:grid-cols-[1fr_280px] md:gap-10">
          <div>
            <h2 className="font-display text-2xl font-bold md:text-3xl">About this night</h2>
            <p className="mt-4 whitespace-pre-line text-base leading-relaxed text-muted-foreground">
              {event.notes ?? "Join us at Plunkets in Dublin city centre. Walk in or book ahead to lock in your spot."}
            </p>
          </div>
          <aside className="space-y-4 rounded-2xl border border-border bg-card/40 p-5 md:p-6">
            <div>
              <p className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">When</p>
              <p className="mt-1 text-sm">{formatLong(event.starts_at)}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Where</p>
              <p className="mt-1 flex items-center gap-1.5 text-sm"><MapPin size={13} /> Plunkets Pub, Dublin City Centre</p>
            </div>
            <button
              onClick={() => openModal({ bookingType: bookingTypeForTag(event.tag) })}
              className="mt-2 hidden w-full items-center justify-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold uppercase tracking-wider text-primary-foreground transition hover:opacity-90 md:inline-flex"
            >
              Reserve <ArrowRight size={14} />
            </button>
          </aside>
        </div>
      </section>

      {/* Photo gallery */}
      {event.gallery_urls && event.gallery_urls.length > 0 && (
        <section className="px-5 pb-12 md:px-8 md:pb-20">
          <div className="mx-auto max-w-4xl">
            <div className="mb-6 flex items-end justify-between gap-4">
              <div>
                <p className="flex items-center gap-2 text-[11px] uppercase tracking-[0.35em] text-primary">
                  <ImageIcon size={14} /> Gallery
                </p>
                <h2 className="mt-2 font-display text-2xl font-bold md:text-3xl">From the floor</h2>
              </div>
              <p className="hidden text-xs text-muted-foreground sm:block">
                {event.gallery_urls.length} {event.gallery_urls.length === 1 ? "photo" : "photos"} — tap to expand
              </p>
            </div>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 sm:gap-3 md:grid-cols-4">
              {event.gallery_urls.map((url, i) => (
                <button
                  key={`${url}-${i}`}
                  type="button"
                  onClick={() => setLightboxIndex(i)}
                  className="group relative aspect-square overflow-hidden rounded-lg bg-muted ring-1 ring-border focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  aria-label={`Open photo ${i + 1} of ${event.gallery_urls.length}`}
                >
                  <img
                    src={url}
                    alt={`${event.title} — photo ${i + 1}`}
                    loading="lazy"
                    className="absolute inset-0 h-full w-full object-cover transition duration-500 group-hover:scale-105"
                  />
                  <span className="absolute inset-0 bg-black/0 transition group-hover:bg-black/20" />
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {lightboxIndex !== null && event.gallery_urls && event.gallery_urls.length > 0 && (
        <Lightbox
          images={event.gallery_urls}
          index={lightboxIndex}
          alt={event.title}
          onClose={() => setLightboxIndex(null)}
          onChange={setLightboxIndex}
        />
      )}


      {/* Sticky mobile CTA */}
      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-background/95 px-4 py-3 backdrop-blur md:hidden">
        <div className="mx-auto flex max-w-4xl items-center gap-3">
          <div className="min-w-0 flex-1">
            <p className="truncate text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
              {event.tag ?? "Event"}
            </p>
            <p className="truncate text-sm font-semibold">
              {new Date(event.starts_at).toLocaleString("en-IE", { weekday: "short", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
            </p>
          </div>
          <button
            onClick={() => openModal({ bookingType: bookingTypeForTag(event.tag) })}
            className="inline-flex shrink-0 items-center gap-2 rounded-full bg-primary px-5 py-3 text-sm font-semibold uppercase tracking-wider text-primary-foreground"
          >
            Reserve <ArrowRight size={14} />
          </button>
        </div>
      </div>
    <SiteFooter />
    </main>
  );
}

